import { getFirestore, collection, query, where, getDocs, doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import type { Registration } from '../types';

/** Update existing profiles through explicit registration.userId links only.
 * No email lookup, alias creation or personId reassignment is permitted here.
 * The complete selected edition is validated before the first profile write.
 */
export async function syncUsersFromRegistrations(editionId: string) {
  const db = getFirestore();
  const selected = await getDocs(query(collection(db, 'registrations'), where('editionId', '==', editionId)));
  if (selected.size > 200) throw new Error('Profile sync requires a smaller reviewed registration batch.');
  if (selected.empty) return;
  await runTransaction(db, async tx => {
    const registrations: Registration[] = [];
    for (const original of selected.docs) {
      const current = await tx.get(doc(db, 'registrations', original.id));
      if (!current.exists() || current.data().editionId !== editionId)
        throw new Error('Registration changed during profile sync. Please review and try again.');
      registrations.push(current.data() as Registration);
    }
    const profiles = new Map<string, { ref: ReturnType<typeof doc>; data: any; update: Record<string, any> }>();
    for (const reg of registrations) {
      const userId = reg.userId;
      if (typeof userId !== 'string' || !userId || userId.trim() !== userId || userId.includes('/'))
        throw new Error('Registration has no reviewed user link. Profile sync was not applied.');
      if (!profiles.has(userId)) {
        const ref = doc(db, 'users', userId), snapshot = await tx.get(ref);
        if (!snapshot.exists()) throw new Error('Linked profile is missing. Profile sync was not applied.');
        profiles.set(userId, { ref, data: snapshot.data(), update: {} });
      }
      const profile = profiles.get(userId)!;
      if (!Number.isSafeInteger(profile.data.personId) || profile.data.personId <= 0
        || (reg.personId != null && reg.personId !== profile.data.personId))
        throw new Error('Registration and profile identity require review. Profile sync was not applied.');
      const representing = profile.update.representing ||
        (Array.isArray(profile.data.representing) ? [...profile.data.representing] : []);
      if (reg.representing && !representing.includes(reg.representing)) representing.push(reg.representing);
      profile.update.representing = representing.filter(Boolean);
      const fields: Record<string, unknown> = {
        firstName: reg.firstName, lastName: reg.lastName, nationality: reg.nationality,
        dateOfBirth: (reg as any).dateOfBirth, phoneCountryCode: (reg as any).phoneCountryCode,
        phone: (reg as any).phoneNumber,
      };
      for (const [field, value] of Object.entries(fields)) {
        if (value !== undefined && value !== null && value !== '') profile.update[field] = value;
      }
      // Auth/account email can differ from a historical registration contact.
      // Changing it is not part of this profile-data synchronization.
      profile.update.editionId = editionId;
    }
    for (const profile of Array.from(profiles.values())) {
      tx.set(profile.ref, { ...profile.update, lastSynced: serverTimestamp() }, { merge: true });
    }
  });
}
